import { Injectable } from '@angular/core';
import { createStore, select, withProps } from '@ngneat/elf';
import { Observable, Subject } from 'rxjs';
import { distinctUntilChanged } from 'rxjs/operators';

import {
  AuraState,
  AuraStateUserMetadata,
} from './interfaces/aura-state.model';

// Initialize store
const auraStore = createStore(
  { name: 'aura-state' },
  withProps<AuraState>({
    userMetadata: null,
    isAuthenticated: false,
    userPermissions: null,
  })
);

@Injectable({ providedIn: 'root' })
export class AuraStateRepository {
  /** Exposed slices as observables */
  userMetadata$: Observable<AuraStateUserMetadata | null> = auraStore.pipe(
    select((state) => state.userMetadata),
    distinctUntilChanged()
  );

  isAuthenticated$: Observable<boolean> = auraStore.pipe(
    select((state) => state.isAuthenticated),
    distinctUntilChanged()
  );

  userPermissions$: Observable<string[] | null> = auraStore.pipe(
    // coalesce undefined → null
    select((state) => state.userPermissions ?? null),
    distinctUntilChanged()
  );

  /** Emits when clearSession() is called */
  public _sessionCleared = new Subject<void>();
  public sessionCleared$ = this._sessionCleared.asObservable();

  /**
   * Update session data (e.g. after login/token refresh)
   */
  setSessionData(
    userMetadata: AuraStateUserMetadata | null,
    isAuthenticated: boolean,
    userPermissions?: string[] | null
  ): void {
    auraStore.update((state) => ({
      ...state,
      userMetadata,
      isAuthenticated,
      ...(userPermissions !== undefined && { userPermissions }),
    }));
  }

  /**
   * Clear everything (e.g. on logout)
   */
  clearSession(): void {
    auraStore.update((state) => ({
      ...state,
      userMetadata: null,
      isAuthenticated: false,
      userPermissions: null,
    }));
    this._sessionCleared.next();
  }

  /** Sync getters */
  getUserMetadata(): AuraStateUserMetadata | null {
    return auraStore.getValue().userMetadata;
  }

  getIsAuthenticated(): boolean {
    return auraStore.getValue().isAuthenticated;
  }

  getUserPermissions(): string[] | null {
    return auraStore.getValue().userPermissions ?? null;
  }
}
